package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.Payslip
import kotlinx.serialization.Serializable

@Serializable
data class ShiftFinancials(
    val shiftId: String,
    val rosterId: String,
    val dailyRecordId: String,
    val date: String,
    val ratesSnapshot: RatesSnapshot,
    val costs: ShiftCostBreakdown,
    /** Whose pay this is; lets employees read their own costs (firestore.rules) and follows reassignment. */
    val userId: String? = null
)

@Serializable
data class RatesSnapshot(
    val rateUsed: Double,
    val employerRateUsed: Double,
    val contractType: String, // SystemContractType name
    val currency: String,
    val market: String = "POLAND",
    val plannedHours: Double = 0.0,
    val nightHours: Double = 0.0
)

@Serializable
data class ShiftCostBreakdown(
    /** Gross pay for the planned hours, incl. allowances. */
    val estimatedPayout: Double,
    /** Gross plus the employer's contributions, planned. */
    val employerCost: Double,
    /** Gross pay for the clocked hours (the planned gross until clocked). */
    val actualPayout: Double,
    val otherCosts: Map<String, Double> = emptyMap(),
    val nightAllowance: Double = 0.0,
    val employerContributions: Double = 0.0,
    /** Employer cost for the clocked hours; null until clocked out. */
    val actualEmployerCost: Double? = null
)

@Serializable
data class Adjustment(
    val id: String,
    val userId: String,
    val rosterId: String,
    val type: AdjustmentType,
    val amount: Double,
    val currency: String,
    val description: String,
    val date: String,
    val relatedShiftId: String?,
    val metadata: AuditMetadata
)

@Serializable
enum class AdjustmentType { BONUS, DEDUCTION, EXPENSE_REFUND }

/** A bonus or deduction that cannot be saved (SH-78): the domain says which part is wrong. */
class InvalidAdjustmentException(val problem: Problem) : Exception("Invalid adjustment: $problem") {
    enum class Problem { DESCRIPTION_MISSING, AMOUNT_NOT_POSITIVE, DATE_OUTSIDE_MONTH }
}

/**
 * The month is LOCKED (SH-78): its bonuses, deductions and refunds are history that the
 * accountant may already have used, so nothing is added, changed or removed any more.
 */
class MonthLockedException(val rosterId: String) : Exception("Month $rosterId is locked")

/** One person's month at one workplace: what they earned and what it costs. */
data class EmployeePayroll(
    val userId: String,
    val employeeName: String,
    val contract: SystemContractType,
    val plannedHours: Double,
    val workedHours: Double,
    /** Assigned shifts that have no cost yet (no pay rates for this workplace). */
    val shiftsWithoutCost: Int,
    /** Taxed with the pay. */
    val bonuses: Double,
    /** Taken from the take-home pay. */
    val deductions: Double,
    /** Paid back on top, untaxed. */
    val expenseRefunds: Double,
    val payslip: Payslip,
    /** The hours the pay is for: clocked where clocked, planned otherwise. */
    val paidHours: Double = 0.0,
    /** The month's bonuses, deductions and refunds one by one (SH-78), by date. */
    val adjustments: List<Adjustment> = emptyList(),
    /** The person's shifts of the month, which an adjustment may refer to, by start. */
    val shifts: List<Shift> = emptyList(),
    /**
     * SH-46: the estimate for the planned hours only (as rostered, before any clocking), with the
     * same bonuses, deductions and refunds; [payslip] is the actual one (clocked where clocked).
     * Null where the plan was not worked out (then the plan is the actual).
     */
    val plannedPayslip: Payslip? = null,
    /**
     * SH-57: overtime, Sunday and holiday work and the minimum-wage top-up of the month, already in
     * [payslip]'s gross; null without pay terms. [plannedFactors]: the same for the planned hours.
     */
    val factors: MonthPayFactors? = null,
    val plannedFactors: MonthPayFactors? = null,
    /**
     * SH-147: they work here without pay terms (not the owner, not confirmed as unpaid): their
     * figures are 0 because nothing is known, not because they cost nothing.
     */
    val missingPay: Boolean = false,
    /** SH-147: the owner working without pay terms: 0 because they take no wage. */
    val worksWithoutPay: Boolean = false,
    /**
     * SH-147: the employer cost of their shifts alone, from the cost snapshots, as the Finances
     * screen and the daily results count it (planned, and payable: clocked where clocked). The
     * payslip's employer cost adds the month's own items on top: bonuses, overtime, Sunday and
     * holiday supplements, the minimum-wage top-up, leave pay, a monthly salary, refunds.
     */
    val shiftsEmployerCostPlanned: Double = 0.0,
    val shiftsEmployerCost: Double = 0.0
) {
    /** What reaches the person's account. */
    val toPay: Double get() = payslip.net - deductions + expenseRefunds
    val employerCost: Double get() = payslip.employerCost + expenseRefunds

    /** The planned side (SH-46): gross, pay-out and employer cost for the rostered hours. */
    val plannedGross: Double get() = (plannedPayslip ?: payslip).gross
    val plannedToPay: Double get() = (plannedPayslip ?: payslip).net - deductions + expenseRefunds
    val plannedEmployerCost: Double get() = (plannedPayslip ?: payslip).employerCost + expenseRefunds
}

data class RosterPayroll(
    val rosterId: String,
    val year: Int,
    val month: Int,
    val market: Market,
    val currency: String,
    val employees: List<EmployeePayroll>,
    /** The month is LOCKED: its adjustments can be seen but no longer changed (SH-78). */
    val locked: Boolean = false
) {
    val totalGross: Double get() = employees.sumOf { it.payslip.gross }
    val totalToPay: Double get() = employees.sumOf { it.toPay }
    val totalEmployerCost: Double get() = employees.sumOf { it.employerCost }
    val totalPlannedHours: Double get() = employees.sumOf { it.plannedHours }
    val totalPaidHours: Double get() = employees.sumOf { it.paidHours }
    val totalPlannedToPay: Double get() = employees.sumOf { it.plannedToPay }
    val totalPlannedEmployerCost: Double get() = employees.sumOf { it.plannedEmployerCost }

    /** SH-147: the shifts' employer cost alone (what the Finances screen counts), planned and payable. */
    val totalShiftsEmployerCostPlanned: Double get() = employees.sumOf { it.shiftsEmployerCostPlanned }
    val totalShiftsEmployerCost: Double get() = employees.sumOf { it.shiftsEmployerCost }

    /** SH-147: the people working without pay terms (their labour cost is not known). */
    val missingPay: List<EmployeePayroll> get() = employees.filter { it.missingPay }
}
